import { AssessmentPlanV2 } from '../components/AssessmentAndPlan/types'
import { DifForm, TabDifForm } from '../components/DigitalIntakeForm/types'
import { RetinoscopyData } from '../components/Retinoscopy/models'
import { AppointmentType, TeloAppointment } from './appointment'
import { ChiefComplaint } from './chiefComplaint'
import { Immunizations } from './immunizations'

import { Code } from './coding'
import {
	InstrumentsData,
	PhoropterDataPart,
	PreTestData,
	RX_EYES,
} from './instruments'
import { HistoricalRXDataApi } from './instrumentsApi'
import { LensAddOn } from './lensAddOn'
import {
	ARDoctorInterpretationData,
	BinocularAssessmentData,
	BloodPressureData,
	ColorTestData,
	LensDesignData,
	LensMaterialData,
	ManualData,
	OcularHealthData,
	PachymetryData,
	PupilTestingData,
	StereoTestData,
	VisualAcuitiesData,
	VitalsData,
} from './manual'
import { DateIsoString, Id } from './model'
import { MyopiaCareReport } from './myopia'
import { InternalPatient } from './patient'
import { Surgeon } from './practice'
import { LensDataSingleEye } from './prescription'
import { Store } from './store'
import { SurgeryCode } from './surgeryCondition'
import { CheckboxValue, YesNoValue } from './types'
import { VAScale } from './vaScales'

export const APPOINTMENT_STATUS = [
	'arrived',
	'booked',
	'cancelled',
	'checked-in',
	'confirmed',
	'fulfilled',
	'noshow',
	'pending',
	'proposed',
	'waitlist',
	'active',
	'deleted',
	'billed',
	'expired',
	'missed',
	'entered-in-error',
] as const

export type AppointmentStatus = (typeof APPOINTMENT_STATUS)[number]

export type ExamStatus =
	| AppointmentStatus
	| 'Planned'
	| 'IntakeFormEnded'
	| 'PreTestStarted'
	| 'PreTestFinished'
	| 'RefractionistStarted'
	| 'RefractionistEnded'
	| 'DoctorModeSelected'
	| 'DoctorStarted'
	| 'DoctorEnded'
	| 'LensTrialQuit'
	| 'DoctorEndedWithLensTrial'
	| 'Ended'
	| 'Interrupted'
	| 'Uncompleted'
	| 'Canceled'
	| 'Paused'
	| 'Waiting'

export type MedicalInfo = {
	eyesDilatatedBefore: YesNoValue
	retinalImageBefore: YesNoValue
	pregnant: YesNoValue
	smoke: YesNoValue
	drink: YesNoValue
	medications: YesNoValue
	allergic: YesNoValue
	eyeTrauma: YesNoValue
	stopMedications: YesNoValue
	stopHistory: YesNoValue
	lazy: YesNoValue
	stopDiseases: YesNoValue
	doubleVision: YesNoValue
	surgery: YesNoValue
}

export type IntakeFormBase = MedicalInfo & {
	symptoms: string
}

export type IntakeForm = IntakeFormBase & {
	medicalIssues: string[]
	presentGlassAge: string
	lastExamDate: Date | null
}

export type IntakeFormUser = IntakeFormBase & {
	medicalIssues: CheckboxValue[]
	presentGlassAge: string
	lastExamDate: Date | null
}

export type DataExtraPatientToExam = {
	language: string
	gender: string
	occupation: string
}

export type IntakeFormApi = IntakeFormBase & {
	medicalIssues: string[]
	presentGlassAge: string
	lastExamDate: Date | undefined
}

export type DigitalIntakeForm = TabDifForm

export type ExamHistory = {
	prevStatus: ExamStatus
	currentStatus: ExamStatus
	statusUpdatedAt: Date
}[]

export type LensAddOnsData = LensAddOn[]

export type UpdateLocalPretestDataType = {
	updatedData:
		| {
				historicalRX: HistoricalRXDataApi[]
		  }
		| { visualAcuities: VisualAcuitiesData }
		| { stereoTest: StereoTestData }
		| { colorTest: ColorTestData }
		| { bloodPressure: BloodPressureData }
		| { binocularAssessment: BinocularAssessmentData }
		| { vitalsData: VitalsData }
		| { arDoctorInterpretation: ARDoctorInterpretationData }
} & {
	exam: ExamApi
}

export type PretestDataApi = {
	arDoctorInterpretation: ARDoctorInterpretationData
	binocularAssessment: BinocularAssessmentData
	bloodPressure: BloodPressureData
	colorTest: ColorTestData
	historicalRX: HistoricalRXDataApi[]
	lensAddOns: LensAddOnsData
	lensDesign: LensDesignData
	lensMaterial: LensMaterialData
	ocularHealth: OcularHealthData
	pachymetry: PachymetryData
	pupil: PupilTestingData
	retinoscopy: RetinoscopyData
	stereoTest: StereoTestData
	visualAcuities: VisualAcuitiesData
	vitalsData: VitalsData
}

export type ExamMode = 'remote' | 'store' | 'assignToMe'

export type PrivacyPolicyDocumentSigned = {
	storeDocumentId: Id
	ref: string
	signedDate: string
	name?: string
}

export type DoctorNote = {
	text: string
	authorUsername: string
	timestamp: Date
}

export type DoctorNotesKeys =
	| keyof InstrumentsData
	| keyof Omit<ManualData, 'binocularAssessment'>
	| 'anteriorSegment'
	| 'chiefComplaint'
	| 'extraocular'
	| 'fundus'
	| 'gonioscopy'
	| 'opticalNerve'
	| 'phoria'
	| 'pupil'

export type DoctorNotes = {
	[k in DoctorNotesKeys]?: DoctorNote | undefined
}

export type NextEyeExam =
	| '1 month'
	| '2 months'
	| '3 months'
	| '4 months'
	| '5 months'
	| '6 months'
	| '9 months'
	| '12 months'
	| '13 months'
	| '2 weeks'
	| '1 week'
	| '3 days'
	| '2 days'
	| '1 day'
	| 'As Needed'

export type DoctorInterpretationEye = {
	full: boolean
	note?: DoctorNote
}

export type DoctorConfrontationalVFEye = {
	ftfc: boolean
	note?: DoctorNote
}

export type DoctorInterpretation = {
	OD: DoctorInterpretationEye
	OS: DoctorInterpretationEye
}

export type DoctorConfrontationalVF = {
	OD: DoctorConfrontationalVFEye
	OS: DoctorConfrontationalVFEye
}

export type EyeHealthDiagnosi = {
	diagnosi: string
	note: string
}

export type EyeHealthDiagnosis = {
	lidsConjunctiva: EyeHealthDiagnosi[]
	cornea: EyeHealthDiagnosi[]
	retina: EyeHealthDiagnosi[]
}

export type EyeHealthCondition = {
	conditionName: string
	isSelf?: boolean
	isRelatives?: boolean
	relatives?: string[]
}

export type EyeHealth = {
	medicalHistory?: EyeHealthCondition[]
	diagnosis: EyeHealthDiagnosis
	nextEyeExam?: NextEyeExam
	recommendations?: string
	document?: {
		timestamp: number
		fileName: string
	}
}

export type ReferralDocument = {
	timestamp: number
	fileName: string
	patientSigned?: boolean
	patientSignedAt?: number
	signed: boolean
	sent: boolean
}

export type ReferralFormData = {
	specialty: string
	referralDoctor: Id
	urgent: boolean
	referralReason: string
	referralReasonNote: string
	medicalValues?: PhoropterDataPart
	document?: ReferralDocument
}

export type HistoryDoctor = {
	doctor: string
	createdAt: Date
}

export type ExamChart = {
	container: string
	timestamp: number
	fileName: string
}

export type ExamMedicalReport = {
	id: Id
	container?: string
	timestamp?: number
	fileName?: string
	description: string
	attachment?: ExamDocument
	templateRef?: string
	templateBody?: string
	lastUpdateBy: string
	status: 'created' | 'cancelled'
}

export type AdditionalPrescription = ExamChart

export type ExamDocuments = {
	additionalPrescriptions?: AdditionalPrescription[]
	attachments?: ExamDocument[]
	charts?: ExamChart[]
	eyeHealth?: EyeHealth
	medicalReports?: ExamMedicalReport[]
	privacyPolicyDocuments?: PrivacyPolicyDocumentSigned[]
	referralForm?: ReferralFormData
	myopiaCare?: MyopiaCareReport
}

export type MedicationMetadata = {
	endDate?: string
	unableToReview?: boolean
	tobacco?: boolean
}

export type Medication = MedicationMetadata & {
	dispense?: number
	drugInfo: string
	genericName: string
	patientFriendlySIG: string
	physicianName: string
	prescriptionDate: string // i.e.: 2022-05-10T04:19:42.567-05:00
	prescriptionGuid: string
	refills: number
	rXcui?: number
	status: string
}

export type OrderStatusType =
	| 'draft'
	| 'created'
	| 'resultsReceived'
	| 'closed'
	| 'cancelled'
	| 'moved'
	| 'ordered'

export type Order = {
	id: string
	orderType: OrderType
	orderContextId?: string
	orderContextName?: string
	orderSubtype?: Omit<OrderType, 'alreadyRequested'>
	alreadyRequestedOrderDate?: Date
	activity?: 'today' | 'schedule' | 'referral'
	reasonIcd?: string
	reasonCpt: string
	snomedctTerm?: string
	caseSignificanceId?: string
	reasonSnomedCt?: string
	reasonLoinc?: string
	status: OrderStatusType
	schedule?: Schedule
	laboratoryTest?: LaboratoryTest
	specialTest?: SpecialTest
	referral?: {
		type?: string
		from?: string
		useStoreReferralDoctor: boolean
		specialty?: string
		referralDoctor?: string
		referralDoctorName?: string
		referralDoctorManual?: {
			fullName?: string
			emailAddress?: string
			phone?: string
			practice?: string
			address?: string
			city?: string
			state?: string
			zip?: string
		}
		referralDoctorEmail?: string
		referralReason?: string
		description?: string
		subject?: string
		message?: string
		selectedExams?: any
	}
	results: OrderResult[]
	document?: ReferralDocument
	note?: string
	createdAt?: Date
	cancelledReasonWhy?: string
	createdBy: string
	examStatus?: ExamStatus
	examId?: string
	duplicatedFrom?: string
	duplicatedFromDate?: Date
}

export type OrderType =
	| 'referral'
	| 'laboratory'
	| 'specialTest'
	| 'alreadyRequested'

export type OrderResult = {
	id: string
	attachments?: ExamOrderResultDocument[]
	note?: string
	resultCode?: {
		value?: string
		description?: string
	}
	stdCode?: string
	result?: {
		value: string
		units: string
		range?: string
		abnormalFlag?: string
	}
	date?: Date
	laboratory?: string
	specimen?: {
		source?: string
		disposition?: string
	}
	diagnosticImagingReport?: boolean
	diagnosticImagingReportNote?: string
	images?: ExamDocument[]
	createdBy: string
	createdAt: Date
}

export type OrderResultUnit = {
	abbreviation: string
	label: string
}

export type LaboratoryTest = {
	cpt?: {
		code: string
		description: string
	}
	loinc?: {
		code: string
		description: string
	}
}

export type SpecialTest = {
	cpt?: {
		code: string
		description?: string
	}
}

export type Schedule = {
	scheduleIn?: number
	unit?: ScheduleUnit
	approxDate?: Date
}

export type ScheduleUnit =
	| 'days'
	| 'weeks'
	| 'months'
	| 'years'
	| 'specificDate'

export type PagesKeys =
	| 'dif'
	| 'pretest'
	| 'refraction'
	| 'auxiliary'
	| 'contactLenses'
	| 'ocularHealth'
	| 'otherTest'
	| 'assessmentPlan'
	| 'orders'
	| 'visionTherapy'

export enum ExamPage {
	Dif = 'dif',
	Pretest = 'pretest',
	Refraction = 'refraction',
	Auxiliary = 'auxiliary',
	ContactLenses = 'contactLenses',
	OcularHealth = 'ocularHealth',
	OtherTest = 'otherTest',
	AssessmentPlan = 'assessmentPlan',
	Orders = 'orders',
	VisionTherapy = 'visionTherapy',
}

export enum ExamStep {
	Summary = 0,
	Anamnesis = 1,
	PreTest = 2,
	Refraction = 3,
	Auxiliary = 4,
	ContactLenses = 5,
	OcularHealth = 6,
	Surgery = 7,
	OtherTest = 8,
	VisionTherapy = 9,
	AssessmentPlan = 10,
	Consultation = 11,
	Orders = 12,
	EHRExport = 13,
	Attachment = 14,
	Documents = 15,
	ExamUpdates = 16,
}

export enum AnamnesisSteps {
	ChiefComplaint = 0,
	IntakeForm = 1,
	MedicationHistory = 2,
}

export enum RefractionSteps {
	ClassicExam = 0,
	MyopiaExam = 1,
}

export enum ConsultationSteps {
	ClinicalData = 0,
	Prescriptions = 1,
	AdditionalForms = 2,
	DiseaseAndTreatment = 3,
	VisualSimulation = 4,
}

export enum AssessmentAndPlanSteps {
	AssessmentPlan = 0,
	Procedure = 1,
	ImplantableDevices = 2,
	ScreeningProcedures = 3,
	Goals = 4,
	CodesToBill = 5,
}

export enum OrdersSteps {
	Orders = 0,
}

export const enum ExamUpdatesSteps {
	EmergencyAccess = 0,
	ExamUpdates = 1,
}

export type PanelsKeys =
	| 'personalInfo'
	| 'insuranceInfo'
	| 'healthHistory'
	| 'visionHistory'
	| 'patient'
	| 'myopiaCare'
	| 'oct'
	| 'retinalImaging'
	| 'lensmeter'
	| 'historicalRx'
	| 'autorefraction'
	| 'tonometer'
	| 'visualFields'
	| 'videoSlitLamp'
	| 'vitalsData'
	| 'visualAcuities'
	| 'stereoTest'
	| 'colorTest'
	| 'bloodPressure'
	| 'pupil'
	| 'binocularAssessment'
	| 'refraction'
	| 'retinoscopy'
	| 'subjectiveRefraction'
	| 'cycloplegicRefraction'
	| 'finalRefraction'
	| 'vergeancesHorizontal'
	| 'vergeancesVertical'
	| 'pursuitsAndSaccades'
	| 'mainPage'
	| 'tab'
	| 'myopia'

export type ExamPagePanels = {
	[key in PanelsKeys]?: boolean
}

export type ExamPages = {
	[key in PagesKeys]: ExamPagePanels
}

export type FhirIdentifiers = {
	fhirAppointmentId?: string
	fhirEncounterId?: string
	fhirPatientId?: string
	fhirPatientUpriseId?: string
}

export type LockedByHistoryItem = {
	username: string
	timestamp: DateIsoString
	role?: string
}

export type ExamLink = {
	id: string
	url: string
	timestamp: number
	linkTextOptional: string
	description: string
	uploadedBy: string
}

export type ExamBase = ExamDocuments & {
	_id: Id
	appointmentId: Id
	assessmentPlan?: AssessmentPlanV2
	auxiliary?: AuxiliaryData
	canRefreshDocuments?: boolean
	chartAvailable?: boolean
	checkedInAt?: DateIsoString
	chiefComplaintCategories?: ChiefComplaint[]
	cockpitTechnician?: string
	coding?: Code[]
	confrontationalVF?: DoctorConfrontationalVF
	createdAt: DateIsoString
	dif: DifForm
	doctor: string
	doctorInterpretation?: DoctorInterpretation
	doctorName?: string
	doctorNotes: DoctorNotes
	doctorSurname?: string
	endDateTime?: Date
	examType?: AppointmentType // TODO: migrate old exams to insert the examType field and then make it mandatory
	externalAppointment: TeloAppointment
	externalDif?: DigitalIntakeForm // TabDifForm
	fhirIdentifiers: FhirIdentifiers
	gender: string
	goals?: Goal[]
	healthConcerns?: string[]
	history: ExamHistory
	historyDoctor?: HistoryDoctor[]
	implantableDevices?: ImplantableDevice[]
	intakeForm?: IntakeForm
	internalPatient: InternalPatient
	interruptedBy?: string
	interruptedByRole?: string
	interruptionReason?: string
	isTest?: boolean
	language: string
	localTechnician: string
	localTechnicianName?: string
	localTechnicianSurname?: string
	lockedBy?: string
	lockedByHistory?: LockedByHistoryItem[]
	medicalReportNote?: string
	medications?: Medication[]
	immunizations?: Immunizations[]
	mode?: ExamMode
	noRxNotes?: string
	occupation: string
	orders?: Order[]
	pages: ExamPages
	patientId: string
	previousExamId?: string
	procedures?: Procedure[]
	readonly?: boolean
	refractionist: string
	screeningProcedures?: ScreeningProcedure[]
	startDateTime?: Date
	store: Store
	updatedAt: Date
	updatedBy: string
	vaScaleUsed: VAScale
	surgeries?: ExamSurgery[]
	codingUpriseId?: number
	links?: ExamLink[]
}

export type ExamApi = ExamBase & {
	status: ExamStatus
	preTest: PretestDataApi
	contactLensesTrial?: ContactLensesApi[]
	contactLensesHistory?: ContactLensesHistory[]
	otherTest?: OtherTest[]
	visionTherapy?: VisionTherapy
}

export type Exam = ExamBase & {
	status: ExamStatus
	preTest: PreTestData
	discardPhoropterReadingsBefore: number
	phoropterIsResetting: boolean
	previousExamId?: string
	contactLensesTrial?: ContactLenses[]
	contactLensesHistory?: ContactLensesHistory[]
	otherTest?: OtherTest[]
	visionTherapy?: VisionTherapy | VisionTherapyApi
}

export interface StrippedExamHasDocuments
	extends Record<keyof ExamWorklistDocuments, boolean> {
	prescription: boolean
	surgeries: boolean
}

export type StrippedExam = {
	_id: string
	appointmentId: string
	contactLensesHistory?: ContactLensesHistory[]
	createdAt: DateIsoString
	checkedInAt?: DateIsoString
	doctor?: string
	doctorName?: string
	doctorNotes: DoctorNotes
	doctorSurname?: string
	endDateTime?: Date
	examType: AppointmentType
	hasCharts?: boolean
	history: ExamHistory
	historyDoctor?: HistoryDoctor[]
	internalPatient: InternalPatient
	interruptionReason?: string
	isStripped: boolean
	language: string
	localTechnician: string
	localTechnicianName?: string
	localTechnicianSurname?: string
	mode?: ExamMode
	orders?: Order[]
	patientId: string
	privacyPolicyDocuments: PrivacyPolicyDocumentSigned[]
	startDateTime?: Date
	status: ExamStatus
	store: Store
	externalAppointment: TeloAppointment
	updatedAt: Date
	contactLensesTrial?: ContactLenses[]
	hasDocuments: Partial<StrippedExamHasDocuments>
}

export type NewExamData = {
	appointmentId: Id
	externalDif?: DigitalIntakeForm
	doctorNotes: DoctorNotes
	examType: string
	intakeForm?: IntakeFormApi
	internalPatient: Id
	isTest?: boolean
	localTechnician?: string
	localTechnicianName?: string
	localTechnicianSurname?: string
	mode: string
	patientId: Id
	privacyPolicyDocuments: PrivacyPolicyDocumentSigned[]
	status?: ExamStatus
	store: Id
	storeAssistant?: string
	externalAppointment: TeloAppointment
	vaScaleUsed: VAScale
	visionTherapy: VisionTherapy
}

export type ExamQuery = {
	statusFrom?: ExamStatus
	statusTo?: ExamStatus
	dateFrom?: string
	dateTo?: string
	states?: string
	stores?: string
	languages?: string
	patientId?: string
	internalPatientId?: Id
	appointmentId?: Id
	storeId?: Id
	mode?: ExamMode
	doctor?: string
	username?: string
}

export type WorklistExamsQuery = {
	chartStatus?: string
	cpt?: string
	dateFrom?: string
	dateTo?: string
	icd10?: string
	loinc?: string
	onlyRemote?: boolean
	skip: number
	snomed?: string
	status?: string
	storeId?: Id
	technician?: string
}

export type WorklistExamCodes = {
	icd10: string[]
	cpt: string[]
	snomed: { code: string; caseSignificanceId: string }[]
	loinc: string[]
}

export type ExamExtra = {
	occupation: string
	gender: string
	language: string
}

export type CLAidedVAValue = {
	distance: string
	near: string
	distanceAdditional?: number
	nearAdditional?: number
}

export type CLOverRefractionValue = {
	sphere: string | null
	cylinder: string | null
	axis: string
}

export type CLEvaluationValue = {
	movement: string
	centration: string
	rotation: string
	rotationDirection: string
	comfort?: string
	coverage?: 'FULL' | 'INCOMPLETE'
}

export type CLMeasureValue = {
	baseCurve: string
	diameter: string
	sphere: string | null
	cylinder: string | null
	axis: string
	add: string
	description?:
		| 'HI'
		| 'HIGH'
		| 'MED'
		| 'MEDIUM'
		| 'MID'
		| 'LO'
		| 'LOW'
		| 'D'
		| 'N'
}

export type CLEvaluation = {
	OS: CLEvaluationValue
	OD: CLEvaluationValue
}

export type CLAidedVA = {
	OD: CLAidedVAValue
	OS: CLAidedVAValue
	OU: CLAidedVAValue
}

export type CLMeasure = {
	OD: CLMeasureValue
	OS: CLMeasureValue
}

export type CLOverRefraction = {
	OD: CLOverRefractionValue
	OS: CLOverRefractionValue
}

export type CLStatus = 'PRESCRIBED' | 'REFUSED' | 'TRIAL' | 'NONE'
export type CLTrialType = {
	value: 'IN-OFFICE' | 'DISPENSED-TODAY' | 'ORDER-FOR-DISPENSE' | 'NOT-TRIED'
	label: string
}
export const CLTrialTypeArray: CLTrialType[] = [
	{
		value: 'IN-OFFICE',
		label: 'lensesTrial.trialTypes.IN-OFFICE',
	},
	{
		value: 'DISPENSED-TODAY',
		label: 'lensesTrial.trialTypes.DISPENSED-TODAY',
	},
	{
		value: 'ORDER-FOR-DISPENSE',
		label: 'lensesTrial.trialTypes.ORDER-FOR-DISPENSE',
	},
	{
		value: 'NOT-TRIED',
		label: 'lensesTrial.trialTypes.NOT-TRIED',
	},
]

export type CLModality = {
	value: 'Daily wear' | 'Extended wear' | 'Continuous wear'
	label: string
}
export const CLModalityArray: CLModality[] = [
	{
		value: 'Daily wear',
		label: 'lensesTrial.trialModalities.dailyWear',
	},
	{
		value: 'Extended wear',
		label: 'lensesTrial.trialModalities.extendedWear',
	},
	{
		value: 'Continuous wear',
		label: 'lensesTrial.trialModalities.continuousWear',
	},
]

export type CatalogueId = { [key in keyof CLMeasure]: string }

export type CLSpecialtyLensType = 'RGP' | 'Scleral' | 'OrthoK' | 'Hybrid'
export type CLSpecialtyLens = {
	OD?: CLSpecialtyLensType
	OS?: CLSpecialtyLensType
}

export type CLAdditionalParameters = {
	OD?: string
	OS?: string
}

export type ContactLenses = {
	aidedVA: CLAidedVA
	catalogueId: CatalogueId
	evaluation: CLEvaluation
	followUpDate?: Date
	measure: CLMeasure
	modality?: CLModality['value']
	newSlExamInProgress: boolean
	notes?: string
	overRefraction: CLOverRefraction
	selectedEye: RX_EYES
	status: CLStatus
	trialType?: CLTrialType['value']
	specialtyLens?: CLSpecialtyLens
	additionalParameters?: CLAdditionalParameters
	timestamp?: number
	doctorUsername?: string
}

export type ContactLensesHistory = {
	catalogueId: CatalogueId
	trialType?: CLTrialType
	modality?: CLModality
	measure: CLMeasure
	useSameProduct: boolean
}

export type OtherTest = {
	code: string
	name: string
	images?: ExamDocument[]
	notes?: string
	attachment?: ExamOtherTestDocument
}

export type ContactLensesApi = Omit<ContactLenses, 'catalogueId'> & {
	catalogueId: {
		L: Id
		R: Id
	}
}

export type ContactLensesBase = {
	brand: string
	vendor: string
	baseCurve: string[]
	diameter: string[]
	sphere: string[]
	cyl: string[]
	add: string[]
	additionalParams: string
	enabled: boolean
}
export type ContactLensesCatalogData = ContactLensesBase & {
	_id: Id
}

export type DiagnosisCatalogData = {
	_id: Id
	Code: string
	Description: string
	CarePlan: string
	Status: string
	Section?: string
	Diagnosi?: string
}

export type HealthDescriptionsCatalogData = {
	diagnosi: string
	section: string
	description: string
}

export interface ExamDocument {
	ref: string
	documentName: string
	documentType?: string
	description?: string
	uploadedBy?: string
	timestamp?: number | string
}

export type ExamOrderResultDocument = ExamDocument & {
	uploaded?: boolean
}

export interface ExamOtherTestDocument extends ExamDocument {
	fileName?: string
	fileSize?: string
}

export type AuxiliaryData = {
	vergeancesHorizontal?: VergeancesData
	vergeancesVertical?: VergeancesVerticalData
	pursuitsAndSaccades?: PursuitSaccadesData
}
export type VergeancesData = {
	bi: {
		dist: VergeancesRowData
		near: VergeancesRowData
	}
	bo: {
		dist: VergeancesRowData
		near: VergeancesRowData
	}
}

export type VergeancesVerticalData = {
	buOS: VergeancesRowData
	buOD: VergeancesRowData
	bdOS: VergeancesRowData
	bdOD: VergeancesRowData
}

export type VergeancesRowData = {
	blur?: string
	break: string
	recovery: string
}

export type PursuitSaccadesData = {
	OD: PursuitSaccadesEyeData
	OS: PursuitSaccadesEyeData
}

export type PursuitSaccadesEyeData = {
	safe: boolean
	abnormal: boolean
	notes: string
}

export type Goal = {
	title: string
	notes?: string
	code?: string
	cid?: string
}

export type ExportToolData = {
	assessmentAndPlanSections?: string[]
	ocularHealthSections?: string[]
	auxiliarySections?: string[]
	medications?: boolean
}

export type Procedure = {
	description?: string
	modifiers?: Modifier[]
	status?: string
	statusOdOs?: string
	plan?: string
	planOdOs?: string
	eye?: string
	notes?: string
	title?: string
	code: string
	images?: ProcedureImage[]
}

export type Modifier = {
	_id: string
	code: string
	description: string
}

export type ProcedureImage = ExamDocument & {
	empty?: boolean
	type: string
}

export type ImplantableDevice = {
	isActive: boolean
	udi: {
		udi: string
		di: string
		lotNumber?: string
		serialNumber?: string
		expirationDate?: string
		expirationDateOriginal: string
		manufacturingDate?: string
		manufacturingDateOriginal?: string
		expirationDateOriginalFormat?: string
		manufacturingDateOriginalFormat?: string
	}
	gudid: {
		device: {
			brandName: string
			versionModelNumber: string
			companyName: string
			MRISafetyStatus: string
			labeledNoNRL: boolean
			deviceHCTP: boolean
			gmdnTerms: {
				gmdn: {
					gmdnPTName: string
				}[]
			}
		}
	}
}

export type ImplantableDeviceError = {
	error: string
}

export type ScreeningProcedure = {
	id: string
	name?: string
	isBack?: boolean
	notes?: string
	images?: ExamDocument[]
	attachment?: ScreeningProcedureFile
}

export type ScreeningProcedureFile = ExamDocument & {
	fileSize?: number
}

export type MyopiaRefraction = {
	R: LensDataSingleEye
	L: LensDataSingleEye
	sensitivity?: number
}

export type WithExamId<T> = T & {
	examId: string
}

export type OrderHistory = Order & {
	examId: string
}

export type Diary = {
	examDate: Date
	modifiedBy?: string
	todayActivity?: string
	nextSessionActivity?: string
}

export type Diaries = {
	current: Diary
	previous: Diary[]
}

export type VisionTherapy = {
	recommendation?: string
	diary: Diary
}

export type VisionTherapyApi = {
	recommendation?: string
	diaries: Diaries
}

export type ExamWorklistDocuments = ExamDocuments & {
	contactLensesTrial?: ContactLenses[]
	orders?: Order[]
	eyeHealthDocument?: EyeHealth['document']
	images?: ExamDocument[]
}

export type WorklistExam = Pick<
	Exam,
	| '_id'
	| 'status'
	| 'mode'
	| 'localTechnician'
	| 'internalPatient'
	| 'history'
	| 'externalAppointment'
	| 'examType'
	| 'doctor'
	| 'checkedInAt'
	| 'dif'
> & {
	storeId: string
	hasCharts: boolean
	updatedAt: string
	remote: boolean
	technicianFullName: string
	location: string
	codes: WorklistExamCodes
	doctorFullName: string
	timezone: string
	rxId?: string
	rxSent?: boolean
	documents: ExamWorklistDocuments
	createdAt: string
	ehr: boolean
}

export type ExamSurgery = {
	id: Id
	procedureDate: Date
	defaultCondition?: Id
	eyeSurgery: 'yes' | 'no' | ''
	eyeSurgerySel?: 'OD' | 'BIN' | 'OS'
	status: 'created' | 'cancelled'
	surgeons: Surgeon[]
	procedureCTPs: SurgeryCode[]
	preDiagnosisICDs: SurgeryCode[]
	postDiagnosisICDs: SurgeryCode[]
	anesthesia: SurgeryCode[]
	meds: string
	note: string
	complications?: string
	createdBy: {
		username: string
		fullName: string
	}
	timestamp: number
	document?: {
		timestamp: number
		fileName: string
		container: string
	}
}
